import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { findings as findingsApi, users as usersApi } from "../lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Plus, Trash2, Send, Loader2, CheckCircle2, XCircle,
  Users as UsersIcon, Search, Check, ListTodo, Clock,
  Check as CheckIcon, X as XIcon, Link2, Copy, Mail, Video, Phone, MessageCircle,
} from "lucide-react";
import { localizedCategory } from "../lib/i18nContent";

const STATUS_META = {
  to_do: { icon: ListTodo, label_key: "finding_status.to_do", classes: "bg-stone-100 text-stone-700 ring-stone-400/20" },
  in_progress: { icon: Clock, label_key: "finding_status.in_progress", classes: "bg-amber-50 text-amber-700 ring-amber-600/20" },
  done: { icon: CheckIcon, label_key: "finding_status.done", classes: "bg-emerald-50 text-emerald-700 ring-emerald-600/20" },
  cannot_do: { icon: XIcon, label_key: "finding_status.cannot_do", classes: "bg-red-50 text-red-700 ring-red-600/20" },
};

const CHANNELS = [
  { id: "sms",      icon: MessageSquare, i18n: "group_sms.channel_sms",      accent: "text-emerald-600" },
  { id: "whatsapp", icon: MessageCircle, i18n: "group_sms.channel_whatsapp", accent: "text-green-600" },
  { id: "email",    icon: Mail,          i18n: "group_sms.channel_email",    accent: "text-blue-600" },
  { id: "teams",    icon: Video,         i18n: "group_sms.channel_teams",    accent: "text-purple-600" },
  { id: "copy",     icon: Link2,         i18n: "group_sms.channel_copy",     accent: "text-stone-700" },
];

/**
 * ShareChecklistDialog (aka GroupSmsDialog)
 * Multi-channel share for a grouped TO DO checklist: SMS, Email (mailto),
 * Microsoft Teams (deep link) and plain "Copy link". All channels share the
 * SAME "TO DO" preview link, which redirects the recipient to the interactive
 * public checklist page /checklist/{token}.
 *
 * Messages are intentionally minimal — the checklist itself lives on the
 * public page and the recipient iterates there.
 */
export default function GroupSmsDialog({ open, onOpenChange, storeName, findings }) {
  const { t, i18n } = useTranslation();

  // Selection
  const [statusIncluded, setStatusIncluded] = useState({
    to_do: true, in_progress: true, done: false, cannot_do: false,
  });
  const [selection, setSelection] = useState({});
  // Channel + recipients
  const [channel, setChannel] = useState("sms");
  const [recipients, setRecipients] = useState([""]);   // phones for sms, emails otherwise
  const [team, setTeam] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Link + message
  const [shareUrl, setShareUrl] = useState("");
  const [creatingLink, setCreatingLink] = useState(false);
  const [message, setMessage] = useState("");
  // Send state
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // === Reset when opened ===
  useEffect(() => {
    if (!open) return;
    setStatusIncluded({ to_do: true, in_progress: true, done: false, cannot_do: false });
    setSelection({});
    setChannel("sms");
    setRecipients([""]);
    setSearch("");
    setShareUrl("");
    setMessage("");
    setResult(null);
  }, [open]);

  // Load team on open
  useEffect(() => {
    if (!open) return;
    setTeamLoading(true);
    usersApi
      .assignable()
      .then((rows) => setTeam(rows || []))
      .catch(() => setTeam([]))
      .finally(() => setTeamLoading(false));
  }, [open]);

  // Bootstrap per-finding selection from status filter
  useEffect(() => {
    if (!open || !findings) return;
    setSelection((prev) => {
      const next = { ...prev };
      for (const f of findings) {
        if (next[f.id] === undefined) next[f.id] = !!statusIncluded[f.status];
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, findings]);

  // Reset recipients row when channel changes (values are format-specific)
  useEffect(() => {
    setRecipients([""]);
  }, [channel]);

  const toggleStatusGroup = (s) => {
    const newVal = !statusIncluded[s];
    setStatusIncluded((prev) => ({ ...prev, [s]: newVal }));
    setSelection((prev) => {
      const next = { ...prev };
      for (const f of findings || []) {
        if (f.status === s) next[f.id] = newVal;
      }
      return next;
    });
  };

  const selectedFindings = useMemo(
    () => (findings || []).filter((f) => selection[f.id]),
    [findings, selection],
  );

  const cleanRecipients = useMemo(
    () => recipients.map((p) => (p || "").trim()).filter(Boolean),
    [recipients],
  );

  // Team filtering: for SMS/WhatsApp we need phone; for Email/Teams we need email.
  const channelField = channel === "sms" || channel === "whatsapp" ? "phone" : "email";
  const filteredTeam = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = team.filter((u) => !!u[channelField]);
    if (q) {
      arr = arr.filter((u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q),
      );
    }
    return arr;
  }, [team, search, channelField]);

  // === Selection map for "already picked" indicator ===
  const pickedSet = useMemo(() => {
    const s = new Set();
    for (const v of cleanRecipients) {
      if (channel === "sms" || channel === "whatsapp") s.add(v.replace(/\D/g, ""));
      else s.add(v.toLowerCase());
    }
    return s;
  }, [cleanRecipients, channel]);

  const togglePerson = (person) => {
    const val = channel === "sms" || channel === "whatsapp" ? person.phone : person.email;
    if (!val) return;
    const key = channel === "sms" || channel === "whatsapp" ? val.replace(/\D/g, "") : val.toLowerCase();
    if (pickedSet.has(key)) {
      setRecipients((arr) => {
        const next = arr.filter((row) => {
          const rk = channel === "sms" || channel === "whatsapp" ? row.replace(/\D/g, "") : row.toLowerCase();
          return rk !== key;
        });
        return next.length === 0 ? [""] : next;
      });
      return;
    }
    setRecipients((arr) => {
      const compact = arr.filter((row) => (row || "").trim());
      if (channel === "sms" && compact.length >= 10) {
        toast.warning(t("sms.max_recipients", { n: 10 }));
        return arr;
      }
      return [...compact, val];
    });
  };

  const addRow = () => setRecipients((p) => [...p, ""]);
  const removeRow = (i) => setRecipients((p) => p.filter((_, idx) => idx !== i));
  const setRow = (i, v) => setRecipients((p) => p.map((x, idx) => (idx === i ? v : x)));

  // === TO DO preview link ===
  const ensureShareLink = async () => {
    if (shareUrl) return shareUrl;
    if (selectedFindings.length === 0) {
      toast.error(t("group_sms.no_selected", "Selecciona al menos un TO DO."));
      return "";
    }
    setCreatingLink(true);
    try {
      const r = await findingsApi.shareChecklist({
        finding_ids: selectedFindings.map((f) => f.id),
        store_name: storeName || "",
        title: `TO DOs · ${storeName || ""}`,
      });
      const url = `${window.location.origin}/api/share/c/${r.share_token}`;
      setShareUrl(url);
      toast.success(t("group_sms.link_ready", "Link listo con imagen TO DO"));
      return url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error", "Error"));
      return "";
    } finally {
      setCreatingLink(false);
    }
  };

  // === Send handlers per channel ===
  const sendSms = async () => {
    const link = await ensureShareLink();
    if (!link) return;
    // Minimal message — just the link (no pre-filled checklist body)
    const body =
      (message.trim() ||
        `[Canales] ${storeName || ""} · ${t("group_sms.sms_default", "Checklist TO DO")}: ${link}`).slice(0, 1500);
    setSending(true);
    setResult(null);
    try {
      const r = await findingsApi.sendGroupSms({
        finding_ids: selectedFindings.map((f) => f.id),
        phones: cleanRecipients,
        message: body,
        store_name: storeName || "",
      });
      setResult(r);
      if (r.ok_count > 0) toast.success(t("sms.sent_n", { n: r.ok_count }));
      if (r.fail_count > 0) toast.warning(t("sms.failed_n", { n: r.fail_count }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("sms.send_error"));
    } finally {
      setSending(false);
    }
  };

  const openMailClient = async () => {
    const link = await ensureShareLink();
    if (!link) return;
    const to = cleanRecipients.join(",");
    const subject = `${t("group_sms.email_subject", "TO DO checklist")} · ${storeName || ""}`;
    const body = message.trim()
      ? `${message.trim()}\n\n${link}`
      : `${t("group_sms.email_default_body", "Abre el checklist interactivo:")}\n${link}`;
    const href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    toast.success(t("group_sms.email_opened", "Cliente de correo abierto"));
  };

  const openTeamsShare = async () => {
    const link = await ensureShareLink();
    if (!link) return;
    // Microsoft Teams "Share to Teams" launcher (opens Teams web/desktop)
    // Docs: https://learn.microsoft.com/microsoftteams/platform/concepts/build-and-test/share-to-teams-from-web-apps
    const preview = `${storeName || ""} · TO DO checklist`;
    // If we have Teams contacts (emails), use chat/deep-link for direct chat;
    // otherwise fall back to the share dialog that lets the user pick channels.
    let url;
    if (cleanRecipients.length > 0) {
      const users = encodeURIComponent(cleanRecipients.join(","));
      const messageText = encodeURIComponent(
        message.trim() || `${t("group_sms.teams_default", "Abre el checklist interactivo:")} ${link}`,
      );
      url = `https://teams.microsoft.com/l/chat/0/0?users=${users}&message=${messageText}&topicName=${encodeURIComponent(preview)}`;
    } else {
      url = `https://teams.microsoft.com/share?href=${encodeURIComponent(link)}&msgText=${encodeURIComponent(preview)}&preview=true`;
    }
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(t("group_sms.teams_opened", "Compartir en Teams"));
  };

  const openWhatsApp = async () => {
    const link = await ensureShareLink();
    if (!link) return;
    const body = (message.trim()
      ? `${message.trim()}\n\n${link}`
      : `${t("group_sms.whatsapp_default", "Abre el checklist interactivo:")} ${link}`);
    // WhatsApp click-to-chat: only supports a single recipient per URL.
    // For multiple picked contacts we open a separate tab per recipient so the
    // user can hit "Send" on each one (WhatsApp does not allow multicast).
    // If no recipients are set we open the general share URL and let the user
    // pick contacts inside WhatsApp.
    if (cleanRecipients.length === 0) {
      const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(body)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      cleanRecipients.forEach((raw, idx) => {
        const digits = (raw || "").replace(/\D/g, "");
        if (!digits) return;
        const url = `https://wa.me/${digits}?text=${encodeURIComponent(body)}`;
        // Stagger opens so browsers do not block popup batches.
        setTimeout(() => window.open(url, "_blank", "noopener,noreferrer"), idx * 250);
      });
    }
    toast.success(t("group_sms.whatsapp_opened", "Abriendo WhatsApp"));
  };

  const copyLink = async () => {
    const link = await ensureShareLink();
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("group_sms.copied", "Link copiado al portapapeles"));
    } catch {
      toast.error(t("group_sms.copy_failed", "No se pudo copiar"));
    }
  };

  const dispatch = () => {
    if (channel === "sms") return sendSms();
    if (channel === "whatsapp") return openWhatsApp();
    if (channel === "email") return openMailClient();
    if (channel === "teams") return openTeamsShare();
    if (channel === "copy") return copyLink();
  };

  // === Send button state ===
  const canSend =
    selectedFindings.length > 0 &&
    !sending &&
    !creatingLink &&
    (channel === "copy" || channel === "whatsapp" ? true : cleanRecipients.length > 0);

  const counts = useMemo(() => {
    const c = { to_do: 0, in_progress: 0, done: 0, cannot_do: 0 };
    for (const f of findings || []) {
      if (c[f.status] !== undefined) c[f.status] += 1;
    }
    return c;
  }, [findings]);

  // === UI helpers ===
  const currentChannelIcon = CHANNELS.find((c) => c.id === channel)?.icon || MessageSquare;
  const ChannelIcon = currentChannelIcon;
  const inputPlaceholder =
    channel === "sms" || channel === "whatsapp" ? t("sms.placeholder", "+1 5551234567")
      : channel === "email" ? "name@example.com"
      : channel === "teams" ? t("group_sms.teams_placeholder", "usuario@empresa.com")
      : "";
  const dispatchLabel = () => {
    if (creatingLink) return t("group_sms.creating_link", "Generando link…");
    if (sending) return t("sms.sending", "Enviando…");
    if (channel === "sms") return t("group_sms.send_sms", "Enviar SMS ({{n}})", { n: cleanRecipients.length });
    if (channel === "whatsapp") return t("group_sms.send_whatsapp", "Abrir WhatsApp ({{n}})", { n: cleanRecipients.length });
    if (channel === "email") return t("group_sms.send_email", "Abrir correo ({{n}})", { n: cleanRecipients.length });
    if (channel === "teams") return t("group_sms.send_teams", "Compartir en Teams");
    return t("group_sms.copy_link", "Copiar link");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="group-sms-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <ChannelIcon size={20} className="text-[#1E3A8A]" />
            {t("group_sms.title", "Compartir checklist")}
          </DialogTitle>
          <p className="text-xs text-stone-500">
            {t("group_sms.subtitle_multichannel", "Envía la lista de pendientes por SMS, correo, Teams o copia el link. Todos abren la misma página interactiva.")}
          </p>
        </DialogHeader>

        {/* Store header */}
        <div className="rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-600 border border-stone-200">
          <div className="font-semibold text-stone-700 text-sm">{storeName}</div>
          <div>{t("group_sms.total_findings", "{{n}} hallazgos en total", { n: findings?.length || 0 })}</div>
        </div>

        {/* Channel selector */}
        <div className="mt-3">
          <label className="canales-overline">{t("group_sms.channel_label", "Canal")}</label>
          <div className="mt-1.5 grid grid-cols-5 gap-1.5" data-testid="group-sms-channel-picker">
            {CHANNELS.map((c) => {
              const Icon = c.icon;
              const active = channel === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setChannel(c.id)}
                  data-testid={`group-sms-channel-${c.id}`}
                  className={`flex flex-col items-center gap-1 rounded-lg border py-2 px-2 text-[11px] font-semibold transition-all ${
                    active
                      ? "bg-[#0B1220] border-[#0B1220] text-white"
                      : "bg-white border-stone-200 text-stone-700 hover:border-stone-400"
                  }`}
                >
                  <Icon size={16} className={active ? "" : c.accent} />
                  {t(c.i18n)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Status filter */}
        <div className="mt-3">
          <label className="canales-overline">{t("group_sms.include_statuses", "Estados a incluir")}</label>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="group-sms-status-filter">
            {["to_do", "in_progress", "done", "cannot_do"].map((s) => {
              const meta = STATUS_META[s];
              const Icon = meta.icon;
              const active = statusIncluded[s];
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatusGroup(s)}
                  data-testid={`group-sms-status-${s}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] border transition-all ${
                    active
                      ? "bg-[#0B1220] border-[#0B1220] text-white"
                      : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
                  }`}
                >
                  <Icon size={11} />
                  {t(meta.label_key)}
                  <span className="opacity-70 ml-0.5">({counts[s] || 0})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Findings list */}
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <label className="canales-overline">{t("group_sms.findings_included", "TO DOs incluidos")}</label>
            <span className="text-[11px] text-stone-500">
              {selectedFindings.length} / {findings?.length || 0}
            </span>
          </div>
          <div className="mt-2 border border-stone-200 rounded-lg divide-y max-h-48 overflow-y-auto" data-testid="group-sms-findings-list">
            {(findings || []).length === 0 && (
              <div className="p-3 text-sm text-stone-500 text-center">
                {t("group_sms.empty", "No hay hallazgos para esta tienda.")}
              </div>
            )}
            {(findings || []).map((f) => {
              const meta = STATUS_META[f.status] || STATUS_META.to_do;
              const Icon = meta.icon;
              const checked = !!selection[f.id];
              return (
                <label
                  key={f.id}
                  className={`flex items-start gap-2.5 p-2.5 text-xs cursor-pointer transition-colors ${
                    checked ? "bg-emerald-50/40" : "hover:bg-stone-50"
                  }`}
                  data-testid={`group-sms-finding-${f.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setSelection((prev) => ({ ...prev, [f.id]: e.target.checked }))}
                    className="mt-0.5 accent-[#0B1220]"
                    data-testid={`group-sms-check-${f.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-stone-700">{localizedCategory(f.category, i18n.language)}</span>
                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ring-1 ring-inset ${meta.classes}`}>
                        <Icon size={9} />
                        {t(meta.label_key)}
                      </span>
                      {f.commitment_date && (
                        <span className="text-[10.5px] text-stone-500">📅 {f.commitment_date}</span>
                      )}
                    </div>
                    <div className="text-stone-600 mt-0.5 line-clamp-2">{f.description}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Recipient picker (hidden for copy) */}
        {channel !== "copy" && (
          <>
            <div className="mt-3">
              <div className="flex items-center justify-between">
                <label className="canales-overline flex items-center gap-1.5">
                  <UsersIcon size={12} /> {t("group_sms.pick_contacts", "Elige contactos")}
                </label>
                <span className="text-[11px] text-stone-400">
                  {teamLoading
                    ? t("common.loading")
                    : t("group_sms.contacts_available", "{{n}} disponibles", { n: filteredTeam.length })}
                </span>
              </div>

              {team.length > 0 && (
                <div className="relative mt-2">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("sms.team_search", "Buscar por nombre, correo o rol…")}
                    className="pl-7 h-8 text-sm"
                    data-testid="group-sms-team-search"
                  />
                </div>
              )}

              {!teamLoading && filteredTeam.length === 0 && (
                <div className="mt-2 rounded-lg border border-dashed border-stone-300 bg-stone-50 px-3 py-2 text-[12px] text-stone-500">
                  {channel === "sms" || channel === "whatsapp"
                    ? t("sms.team_empty", "Ningún usuario tiene teléfono registrado.")
                    : t("group_sms.no_emails", "Ningún usuario tiene correo registrado para este canal.")}
                </div>
              )}

              {filteredTeam.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1" data-testid="group-sms-team-list">
                  {filteredTeam.map((u) => {
                    const val = channel === "sms" || channel === "whatsapp" ? u.phone : u.email;
                    const key = channel === "sms" || channel === "whatsapp" ? (val || "").replace(/\D/g, "") : (val || "").toLowerCase();
                    const selected = pickedSet.has(key);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => togglePerson(u)}
                        data-testid={`group-sms-team-chip-${u.id}`}
                        className={`group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] border transition-all ${
                          selected
                            ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                            : "bg-white border-stone-200 text-stone-700 hover:border-[#0B1220] hover:bg-stone-50"
                        }`}
                      >
                        {selected ? <Check size={11} className="text-emerald-600" /> : <Plus size={11} className="text-stone-400 group-hover:text-[#0B1220]" />}
                        <span className="font-medium">{u.name}</span>
                        <span className="text-stone-400 text-[10.5px]">
                          {channel === "sms" || channel === "whatsapp"
                            ? <><Phone size={9} className="inline mb-0.5" /> </>
                            : <><Mail size={9} className="inline mb-0.5" /> </>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-3">
              <div className="flex items-center justify-between">
                <label className="canales-overline">
                  {channel === "sms"
                    ? t("sms.recipients", "Teléfonos destino")
                    : channel === "whatsapp"
                    ? t("group_sms.whatsapp_label", "Números WhatsApp")
                    : t("group_sms.emails_label", "Correos destino")}
                </label>
                <span className="text-[11px] text-stone-400">
                  {cleanRecipients.length}{channel === "sms" ? "/10" : ""}
                  {(channel === "sms" || channel === "whatsapp") && ` · ${t("sms.format_hint")}`}
                </span>
              </div>
              <div className="mt-2 space-y-2">
                {recipients.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={p}
                      onChange={(e) => setRow(i, e.target.value)}
                      placeholder={inputPlaceholder}
                      inputMode={channel === "sms" || channel === "whatsapp" ? "tel" : "email"}
                      className="tabular-nums"
                      data-testid={`group-sms-recipient-${i}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeRow(i)}
                      disabled={recipients.length <= 1}
                      className="shrink-0"
                      data-testid={`group-sms-remove-${i}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
                {(channel !== "sms" || recipients.length < 10) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addRow}
                    className="gap-1.5"
                    data-testid="group-sms-add-row"
                  >
                    <Plus size={14} /> {channel === "sms" || channel === "whatsapp" ? t("sms.add_phone") : t("group_sms.add_email", "Agregar otro correo")}
                  </Button>
                )}
              </div>
              {channel === "whatsapp" && cleanRecipients.length > 1 && (
                <p className="text-[10.5px] text-amber-600 mt-1.5">
                  ⚠️ {t("group_sms.whatsapp_multi_hint", "WhatsApp abre un chat por destinatario. Envía cada uno manualmente.")}
                </p>
              )}
              {channel === "whatsapp" && cleanRecipients.length === 0 && (
                <p className="text-[10.5px] text-stone-500 mt-1.5">
                  💡 {t("group_sms.whatsapp_empty_hint", "Sin destinatarios se abre WhatsApp con el mensaje listo — tú eliges a quién enviárselo.")}
                </p>
              )}
            </div>
          </>
        )}

        {/* Shareable TO DO link (auto-generated on send) */}
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <label className="canales-overline">{t("group_sms.share_link_label", "Link con imagen TO DO")}</label>
            {shareUrl && <span className="text-[11px] text-emerald-700">✓ {t("group_sms.link_ready_short", "Listo")}</span>}
          </div>
          {shareUrl ? (
            <div className="mt-1.5 flex items-center gap-2">
              <Input value={shareUrl} readOnly className="text-[11px] font-mono" data-testid="group-sms-share-url" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copyLink}
                className="shrink-0 gap-1"
                data-testid="group-sms-copy-btn"
                title={t("group_sms.copy_link", "Copiar link")}
              >
                <Copy size={13} />
              </Button>
            </div>
          ) : (
            <p className="text-[10.5px] text-stone-500 mt-1.5">
              {t("group_sms.share_link_hint_short", "Se generará automáticamente al enviar. Muestra la imagen 'TO DO' cuando el destinatario reciba el link.")}
            </p>
          )}
        </div>

        {/* Optional message */}
        {channel !== "copy" && (
          <div className="mt-3">
            <label className="canales-overline">
              {t("group_sms.optional_note", "Nota opcional (se antepone al link)")}
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
              placeholder={t("group_sms.optional_note_placeholder", "Ej: Prioridad esta semana, revisar antes del viernes…")}
              className="mt-1 min-h-[72px] text-sm"
              data-testid="group-sms-message"
            />
            <p className="text-[10.5px] text-stone-400 mt-1">
              {t("group_sms.no_prefill_hint", "El link se envía solo — el destinatario abre la lista interactiva y actualiza cada TO DO (foto + comentario requeridos).")}
            </p>
          </div>
        )}

        {/* Results (SMS only) */}
        {result && (
          <div className="mt-3 rounded-lg border border-stone-200 p-3" data-testid="group-sms-results">
            <div className="text-xs text-stone-500 mb-2">
              {t("sms.summary", { ok: result.ok_count, fail: result.fail_count })} · {t("group_sms.per_findings", "{{n}} hallazgos", { n: result.finding_count })}
            </div>
            <ul className="space-y-1.5">
              {(result.results || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  {r.ok ? <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" /> : <XCircle size={14} className="text-red-600 mt-0.5 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-xs">{r.phone}</span>
                    {r.ok
                      ? <span className="ml-2 text-[11px] text-stone-500">{r.status || "queued"}</span>
                      : <span className="ml-2 text-[11px] text-red-600">{r.error}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            {t("common.close")}
          </Button>
          <Button
            onClick={dispatch}
            disabled={!canSend}
            className="brand-button gap-1.5"
            data-testid="group-sms-send-button"
          >
            {(sending || creatingLink)
              ? <Loader2 size={14} className="animate-spin" />
              : (channel === "copy" ? <Copy size={14} /> : <Send size={14} />)}
            {dispatchLabel()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
